<template>
  <el-card
    style="height: 100%; width: 100%"
    body-class="flex flex-col gap-4"
    :body-style="{
      height: '100%',
      width: '100%',
    }"
    shadow="always">
    <ApplicationDetailsLicencesTable
      v-if="application && currentSubAccount"
      ref="licenseTable"
      v-model:sub-account-id="selectedSubAccountId"
      data-tutorial-id="application-details-licences-card"
      :application="application"
      :application-licenses="allSoftwareLicenses"
      @licenses-changed="handleLicensesChanged"
      @export-licenses="handleExportLicenses"
      @import-licenses="handleImportLicenses"
      @open-add-user="handleOpenAddUser" />
  </el-card>

  <ExportDialog
    v-model:visibility="visibilityExportDialog"
    :sub-account-id="selectedSubAccountId"
    :account-filter="licenseTable?.accountFilter"
    :application-id="application?._id" />

  <SmDialog
    v-model:visibility="showLicenseImportDialog"
    :title="i18n.t('licenseCsvImport.csvImportHeading')">
    <AccountCsvImport
      v-if="application"
      :application-id="application._id"
      @successful-import="handleSuccessfulImport" />
  </SmDialog>

  <AccountAddDialog
    v-if="application"
    v-model:visibility="visibilityAccountAddDialog"
    :software-licenses="allSoftwareLicenses"
    :application="application"
    :sub-account="currentSubAccount ?? undefined"
    @user-added="handleLicensesChanged" />
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { SoftwareLicense, SubAccount_Output } from '@/client'
  import { AccountFilters } from '@/stores/account/account.filter'
  import { useApplicationDetailsStore } from '@/stores/ApplicationDetailsView.store'
  import { useRoute } from 'vue-router'

  const i18n = useI18n()
  const route = useRoute()
  const applicationDetailsStore = useApplicationDetailsStore()

  const applicationIdParam = computed(() => {
    const param = route.params.id
    return Array.isArray(param) ? param[0] : (param as string)
  })

  watch(
    applicationIdParam,
    (id) => {
      if (id) {
        applicationDetailsStore.loadApplicationDetails(id)
      }
    },
    { immediate: true }
  )

  const application = computed(() => applicationDetailsStore.application)
  const allSoftwareLicenses = computed<SoftwareLicense[]>(
    () => applicationDetailsStore.licences
  )

  const licenseTable = ref<{ accountFilter?: AccountFilters }>()

  const selectedSubAccountId = ref<string>()

  const currentSubAccount = computed<SubAccount_Output | null>(() => {
    const app = application.value
    if (!app?.sub_accounts) return null

    if (selectedSubAccountId.value) {
      return app.sub_accounts[selectedSubAccountId.value] ?? null
    }

    const firstKey = Object.keys(app.sub_accounts)[0]
    if (!firstKey) return null
    return app.sub_accounts[firstKey] ?? null
  })

  function getRouteSubAccountId(param: unknown): string | null {
    if (Array.isArray(param)) {
      return typeof param[0] === 'string' ? param[0] : null
    }

    return typeof param === 'string' ? param : null
  }

  watch(
    [() => route.params.subAccountId, application],
    ([newSubAccountId, app]) => {
      if (!app) return

      const subAccounts = app.sub_accounts
      if (!subAccounts) {
        selectedSubAccountId.value = undefined
        return
      }

      const routeSubAccountId = getRouteSubAccountId(newSubAccountId)

      if (routeSubAccountId && subAccounts[routeSubAccountId]) {
        selectedSubAccountId.value = routeSubAccountId
        return
      }

      if (
        selectedSubAccountId.value &&
        subAccounts[selectedSubAccountId.value] !== undefined
      ) {
        return
      }

      const firstKey = Object.keys(subAccounts)[0]
      selectedSubAccountId.value = firstKey
    },
    { immediate: true }
  )

  async function handleLicensesChanged() {
    await applicationDetailsStore.refreshLicences()
  }

  const visibilityExportDialog = ref(false)
  const showLicenseImportDialog = ref(false)
  const visibilityAccountAddDialog = ref(false)

  function handleExportLicenses() {
    visibilityExportDialog.value = true
  }

  function handleImportLicenses() {
    showLicenseImportDialog.value = true
  }

  function handleOpenAddUser() {
    visibilityAccountAddDialog.value = true
  }

  function handleSuccessfulImport() {
    showLicenseImportDialog.value = false
    handleLicensesChanged()
  }
</script>
